import React from "react";
import PropTypes from "prop-types";
import { Modal } from "shared/Modal";
import Button from "shared/Button";
import SearchInput from "shared/@forms/SearchInput";
import Loader from "shared/Loader";
import AlertNotice from "shared/AlertNotice";
import PreviouslyViewed from "shared/PreviouslyViewed";
import { products as productsApi, errorMessage } from "shared/api";
import {
  getPreviouslyViewed,
  recordView,
} from "shared/@utils/previouslyViewed";
import styles from "./index.module.css";

const PV_NAMESPACE = "add_product";

/**
 * Add Product modal — searches NetSuite for a SKU + Title, then on selection
 * creates a new Mongo product (status `in_review`) and hands the new id back
 * to the caller for navigation to the editor.
 *
 * No free-text SKU input — NetSuite owns SKU creation.
 */
export default function AddProductModal({ isOpen, onClose, onCreated }) {
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [status, setStatus] = React.useState("idle"); // idle | searching | done | error
  const [error, setError] = React.useState(null);
  const [selectedSku, setSelectedSku] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const reqIdRef = React.useRef(0);

  const reset = React.useCallback(() => {
    setQuery("");
    setResults([]);
    setStatus("idle");
    setError(null);
    setSelectedSku(null);
    setCreating(false);
  }, []);

  React.useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const handleSearch = React.useCallback((value) => {
    setQuery(value);
    const trimmed = value.trim();
    if (!trimmed) {
      setResults([]);
      setStatus("idle");
      setError(null);
      return;
    }
    const reqId = ++reqIdRef.current;
    setStatus("searching");
    setError(null);
    productsApi
      .searchNetSuite(trimmed)
      .then((items) => {
        if (reqId !== reqIdRef.current) return;
        setResults(items || []);
        setStatus("done");
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        setError(errorMessage(err, "Couldn't search NetSuite."));
        setStatus("error");
      });
  }, []);

  const handleSelect = (item) => {
    // Guard against re-entry: a fast double-click (notably on the
    // Previously-Viewed list, which isn't disabled while creating) would
    // otherwise fire two create calls and produce two duplicate products.
    if (creating) return;
    recordView(PV_NAMESPACE, {
      id: item.sku,
      name: item.title,
      sku: item.sku,
    });
    setSelectedSku(item.sku);
    setCreating(true);
    setError(null);
    productsApi
      .create({ netsuiteSku: item.sku, netsuiteTitle: item.title })
      .then((created) => {
        setCreating(false);
        onCreated(created);
      })
      .catch((err) => {
        setCreating(false);
        setSelectedSku(null);
        // 409 = this NetSuite SKU has already been imported. Point the user
        // at the existing catalog entry instead of dumping the raw JSON body.
        if (err?.status === 409) {
          setError(
            `${
              err?.serverMessage || `SKU "${item.sku}" already exists.`
            } It's already been imported — search for it in the store catalog and edit it there instead.`
          );
          return;
        }
        setError(
          errorMessage(err, "Couldn't add the product. Please try again.")
        );
      });
  };

  const showNoResults =
    status === "done" && results.length === 0 && query.trim().length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Product"
      className={styles.modal}
    >
      <div className={styles.body}>
        <p className={styles.hint}>
          Search NetSuite for a SKU or product title. NetSuite is the source of
          truth for SKUs — you cannot create a SKU here.
        </p>

        <SearchInput
          value={query}
          onSearch={handleSearch}
          timeout={300}
          placeholder="Search NetSuite by SKU or title"
        />

        {status === "searching" && (
          <div className={styles.loaderWrap}>
            <Loader size={40} />
          </div>
        )}

        {status === "error" && error && (
          <div className={styles.alertWrap}>
            <AlertNotice title="Search error">{error}</AlertNotice>
          </div>
        )}

        {status !== "error" && error && (
          <div className={styles.alertWrap}>
            <AlertNotice title="Couldn't add product">{error}</AlertNotice>
          </div>
        )}

        {showNoResults && (
          <div className={styles.alertWrap}>
            <AlertNotice title="No matches in NetSuite">
              No SKUs match &ldquo;{query}&rdquo;. If this product is new,
              create it in NetSuite first, then come back and import it here.
            </AlertNotice>
          </div>
        )}

        {status === "idle" && !query && (
          <PreviouslyViewed
            items={getPreviouslyViewed(PV_NAMESPACE)}
            onSelect={(item) => {
              handleSelect({ sku: item.sku, title: item.name });
            }}
          />
        )}

        {status === "done" && results.length > 0 && (
          <ul className={styles.results}>
            {results.map((item) => (
              <li key={item.sku} className={styles.resultRow}>
                <div className={styles.resultText}>
                  <span className={styles.resultSku}>{item.sku}</span>
                  <span className={styles.resultTitle}>{item.title}</span>
                </div>
                <Button
                  onClick={() => handleSelect(item)}
                  disabled={creating}
                  title={`Import "${item.title}" from NetSuite and open it in the editor.`}
                >
                  {creating && selectedSku === item.sku ? "Adding…" : "Select"}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.footer}>
          <Button
            onClick={onClose}
            disabled={creating}
            title="Close this dialog without adding a product."
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

AddProductModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};
